// =============================================================================
// BUTTON COMPONENT - Consistent button styling on the Bogwood palette
// =============================================================================

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { ButtonSpinner } from './Spinner.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'icon';
export type ButtonSize = 'sm' | 'md' | 'lg';

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-primary-on hover:bg-primary-hover active:bg-primary-press ' +
    'disabled:bg-primary/50',
  secondary:
    'bg-surface-2 text-ink-1 border border-line-2 hover:bg-surface-sunken ' +
    'disabled:bg-surface-2/60',
  ghost:
    'bg-transparent text-ink-2 hover:bg-surface-2 hover:text-ink-1',
  danger:
    'bg-secondary text-secondary-on hover:bg-rowan-700 disabled:bg-secondary/50',
  icon:
    'bg-transparent text-ink-3 hover:bg-surface-2 hover:text-ink-1 p-2 rounded-pill',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      iconPosition = 'left',
      children,
      className = '',
      disabled,
      ...props
    },
    ref
  ) => {
    const isIconOnly = variant === 'icon';
    const baseClasses = isIconOnly
      ? variantClasses.icon
      : `${variantClasses[variant]} ${sizeClasses[size]} rounded-xl font-medium transition-colors duration-200`;

    return (
      <button
        ref={ref}
        className={`
          inline-flex items-center justify-center gap-2
          disabled:cursor-not-allowed disabled:opacity-50
          focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-1
          ${baseClasses}
          ${className}
        `}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <ButtonSpinner />
            {children && <span>{children}</span>}
          </>
        ) : (
          <>
            {icon && iconPosition === 'left' && icon}
            {children}
            {icon && iconPosition === 'right' && icon}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

// Icon-only button shorthand
interface IconButtonProps extends Omit<ButtonProps, 'variant' | 'children'> {
  icon: ReactNode;
  label: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, label, className = '', ...props }, ref) => {
    return (
      <Button
        ref={ref}
        variant="icon"
        aria-label={label}
        title={label}
        className={className}
        {...props}
      >
        {icon}
      </Button>
    );
  }
);

IconButton.displayName = 'IconButton';
